import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"

import AlertBanner from "src/components/AlertBanner"
import Badge from "src/components/Badge"
import CollapsibleBar from "src/components/CollapsibleBar"
import DataTableV2 from "src/components/DataTableV2"
import FormIconTextField from "src/components/Form/FormIconTextField"

import { getDockwalkAuditTrail } from "src/api/DockwalkSummary"

import useDatatable from "src/hooks/use_datatable"

import { statusBadgeColor } from "src/utils/status_badge_color_helper"
import { titlecase } from "src/utils/string_helpers"

const EVENT_ICONS = {
  slip_checked: "check",
  flag_raised: "m-flag",
  flag_resolved: "m-flag",
}

const EVENT_LABELS = {
  slip_checked: "Space checked",
  flag_raised: "Flag raised",
  flag_resolved: "Flag resolved",
}

const eventIconClass = (eventType) => {
  if (["flag_raised", "flag_resolved"].includes(eventType)) {
    return "text-red-400"
  }

  return "text-gray-600"
}

const SummaryItems = ({ items }) => {
  const visibleItems = items.filter(Boolean)

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
      {visibleItems.map((item, index) => (
        <React.Fragment key={`${item.label}-${item.value}`}>
          <div className="flex items-center gap-1">
            <span className="font-semibold text-gray-900">{item.value}</span>
            <span>{item.label}</span>
          </div>
          {index < visibleItems.length - 1 && (
            <span className="text-lg text-gray-300">&bull;</span>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

SummaryItems.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ).isRequired,
}

const AuditTrailTable = ({ marinaSlug, date }) => {
  const [searchInput, setSearchInput] = useState("")

  const table = useDatatable({
    queryKey: ["dockwalkAuditTrail", marinaSlug, date],
    fetchFn: (params) => getDockwalkAuditTrail(marinaSlug, date, params),
    defaultFilters: { search: "" },
    enabled: Boolean(marinaSlug && date),
    infinite: true,
    syncFiltersToUrl: false,
  })

  const columns = useMemo(
    () => [
      {
        key: "createdAt",
        header: "Time",
        sortable: true,
        cardRole: "detail",
        cardOrder: 0,
      },
      {
        key: "eventType",
        header: "Event Type",
        sortable: true,
        cardRole: "title",
        cardOrder: 0,
        render: (row) => (
          <div className="flex items-center gap-2">
            <i
              className={`icon icon-${
                EVENT_ICONS[row.eventType] || "notification"
              } ${eventIconClass(row.eventType)}`}
            />
            <span>{EVENT_LABELS[row.eventType] || row.eventType}</span>
          </div>
        ),
        cardRender: (row) => (
          <div className="flex items-center gap-2">
            <i
              className={`icon icon-${
                EVENT_ICONS[row.eventType] || "notification"
              } ${eventIconClass(row.eventType)}`}
            />
            <span>{EVENT_LABELS[row.eventType] || row.eventType}</span>
          </div>
        ),
      },
      {
        key: "spaceName",
        header: "Space",
        sortable: true,
        cardRole: "subtitle",
        cardOrder: 0,
        cardRender: (row) =>
          [row.spaceName, row.spaceGroupName].filter(Boolean).join(" • "),
      },
      {
        key: "spaceGroupName",
        header: "Space Group",
        sortable: true,
        cardRole: "hidden",
      },
      {
        key: "userName",
        header: "User",
        sortable: true,
        cardRole: "detail",
        cardOrder: 1,
        render: (row) => row.userEmail || row.userName || "Unknown",
      },
      {
        key: "details",
        header: "Details",
        sortable: true,
        cardRole: "footer",
        cardOrder: 0,
        render: (row) => {
          const display = titlecase(row.details || "")
          if (["Occupied", "Vacant", "Unchecked"].includes(display)) {
            return <Badge color={statusBadgeColor(display)} text={display} />
          }

          return row.details || "No details"
        },
        cardRender: (row) => {
          const display = titlecase(row.details || "")
          if (["Occupied", "Vacant", "Unchecked"].includes(display)) {
            return (
              <Badge
                color={statusBadgeColor(display)}
                text={display}
                variant="small"
              />
            )
          }

          return row.details ? (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Details
              </div>
              <div className="mt-1 text-sm text-gray-900">{row.details}</div>
            </div>
          ) : null
        },
      },
    ],
    []
  )

  const handleScroll = (event) => {
    if (!table.hasNextPage || table.isFetchingNextPage) return

    const { scrollHeight, scrollTop, clientHeight } = event.target
    if (scrollHeight - scrollTop - 200 <= clientHeight) {
      table.fetchNextPage()
    }
  }

  return (
    <CollapsibleBar
      title="Audit Trail"
      summary={
        <SummaryItems
          items={[
            {
              value: table.data?.pagination?.totalCount || 0,
              label: pluralize(
                "event",
                table.data?.pagination?.totalCount || 0
              ),
            },
          ]}
        />
      }
      defaultOpen
      className="shadow-sm"
      ariaLabel="Toggle audit trail section"
    >
      <div className="min-w-0 space-y-4">
        <div className="w-full lg:w-1/2">
          <FormIconTextField
            icon={<i className="icon icon-search-mdi text-xl text-gray-400" />}
            placeholder="Search by space, group, user, or details..."
            value={searchInput}
            onChange={(event) => {
              const value = event.target.value
              setSearchInput(value)
              table.setFilter("search", value.trim())
            }}
            isClearable
            onClearSelection={() => {
              setSearchInput("")
              table.clearFilter("search")
            }}
          />
        </div>

        {table.isError ? (
          <AlertBanner type="error" title="Unable to load audit trail">
            Try reloading the page or switching dates to try again.
          </AlertBanner>
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <DataTableV2
              name="dockwalkAuditTrail"
              columns={columns}
              data={table.records}
              hideActions
              hideSettings
              maxHeight="500px"
              rowCount={8}
              onScroll={handleScroll}
              {...table.tableProps}
            />
          </div>
        )}
      </div>
    </CollapsibleBar>
  )
}

AuditTrailTable.propTypes = {
  marinaSlug: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
}

export default AuditTrailTable
